import { useEffect, useState } from "react";
import api, { list, remove } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { APP_LOGO, fmtIDR, fmtDate } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Plus, Eye, Printer, Trash2, Download, Stamp } from "lucide-react";
import { toast } from "sonner";
import { downloadPdf } from "@/lib/pdf";
import { formatApiError } from "@/lib/api";

export default function Receipts() {
  const { can } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [events, setEvents] = useState([]);
  const [genOpen, setGenOpen] = useState(false);
  const [payId, setPayId] = useState("");
  const [preview, setPreview] = useState(null);

  const load = () => list("receipts").then(setReceipts).catch(() => setReceipts([]));
  useEffect(() => {
    load();
    list("payments").then(setPayments).catch(() => {});
    list("events").then(setEvents).catch(() => {});
  }, []);

  const eventName = (eid) => { const e = events.find((x) => x.id === eid); return e ? `${e.groom} & ${e.bride}` : ""; };
  const receiptedPayIds = new Set(receipts.map((r) => r.payment_id));
  const payOptions = payments.filter((p) => !receiptedPayIds.has(p.id));

  const generate = async () => {
    if (!payId) { toast.error("Pilih pembayaran dulu"); return; }
    try {
      const rec = await api.post(`/receipts/generate/${payId}`).then((r) => r.data);
      toast.success(`Kuitansi ${rec.receipt_number} diterbitkan`);
      setGenOpen(false); setPayId(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await remove("receipts", id); toast.success("Kuitansi dihapus"); load(); };

  return (
    <div>
      <PageHeader overline="Bukti Pembayaran" title="Kuitansi" subtitle="Terbitkan kuitansi resmi dari pembayaran yang tercatat.">
        {can(4) && <Button onClick={() => setGenOpen(true)} data-testid="receipts-generate-btn" className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full"><Plus className="w-4 h-4 mr-1" />Buat Kuitansi</Button>}
      </PageHeader>

      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-beige/60 hover:bg-beige/60 border-gold/20">
                {["No. Kuitansi", "Client", "Keterangan", "Nominal", "Tanggal", "Aksi"].map((h) => <TableHead key={h} className="text-[11px] uppercase tracking-wider text-[#4A4A4A]">{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {receipts.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-[#7A7A7A]">Belum ada kuitansi.</TableCell></TableRow>
              ) : receipts.map((r) => (
                <TableRow key={r.id} className="border-gold/10 hover:bg-beige/30" data-testid="receipt-row">
                  <TableCell className="font-mono text-sm text-burgundy font-semibold">{r.receipt_number}</TableCell>
                  <TableCell className="text-sm font-medium text-[#1A1A1A]">{r.client_name}</TableCell>
                  <TableCell className="text-sm text-[#4A4A4A]">{r.description}</TableCell>
                  <TableCell className="text-sm">{fmtIDR(r.amount)}</TableCell>
                  <TableCell className="text-sm">{fmtDate(r.date)}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <button onClick={() => setPreview(r)} className="p-1.5 text-[#7A7A7A] hover:text-burgundy" data-testid="receipt-preview"><Eye className="w-4 h-4" /></button>
                    {can(4) && <button onClick={() => del(r.id)} className="p-1.5 text-[#7A7A7A] hover:text-red-600" data-testid="receipt-delete"><Trash2 className="w-4 h-4" /></button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-burgundy">Buat Kuitansi</DialogTitle></DialogHeader>
          <p className="text-sm text-[#7A7A7A] -mt-2">Pilih pembayaran yang sudah tercatat. Nomor kuitansi dibuat otomatis dan unik.</p>
          <div className="py-2">
            <Select value={payId} onValueChange={setPayId}>
              <SelectTrigger className="w-full bg-white" data-testid="receipt-payment-select"><SelectValue placeholder="Pilih pembayaran..." /></SelectTrigger>
              <SelectContent>
                {payOptions.length === 0 && <div className="px-3 py-2 text-xs text-[#7A7A7A]">Semua pembayaran sudah punya kuitansi.</div>}
                {payOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{eventName(p.event_id)} — {p.label} ({fmtIDR(p.amount)})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)}>Batal</Button>
            <Button onClick={generate} data-testid="receipt-generate-confirm" className="bg-burgundy hover:bg-burgundy-dark text-white">Terbitkan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
          <VisuallyHidden><DialogTitle>Preview Kuitansi</DialogTitle></VisuallyHidden>
          {preview && <ReceiptDoc rec={preview} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReceiptDoc({ rec }) {
  return (
    <div>
      <div id="receipt-doc" className="p-8 bg-white">
        <div className="flex items-center justify-between border-b-2 border-burgundy pb-5 mb-6">
          <div className="flex items-center gap-3">
            <img src={APP_LOGO} alt="Primrose Organizer" className="h-14 w-auto object-contain" />
            <div>
              <h2 className="font-heading text-2xl text-burgundy leading-none">PRIMROSE ORGANIZER</h2>
              <p className="text-[11px] tracking-[0.2em] text-gold-dark uppercase">Wedding Organizer With You</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-heading text-3xl text-[#1A1A1A]">KUITANSI</p>
            <p className="font-mono text-sm text-[#7A7A7A]">{rec.receipt_number}</p>
          </div>
        </div>

        <div className="space-y-3 text-sm mb-6">
          <Row label="Telah diterima dari" value={rec.client_name} />
          <Row label="Untuk pembayaran" value={rec.description} />
          <Row label="Acara" value={rec.event_name} />
          <Row label="Metode pembayaran" value={rec.method} />
          {rec.reference ? <Row label="Referensi" value={rec.reference} /> : null}
          <Row label="Tanggal" value={fmtDate(rec.date)} />
        </div>

        <div className="bg-beige rounded-xl px-5 py-4 flex items-center justify-between mb-8">
          <span className="text-sm uppercase tracking-wider text-[#4A4A4A] font-semibold">Jumlah Diterima</span>
          <span className="font-heading text-2xl text-burgundy">{fmtIDR(rec.amount)}</span>
        </div>

        <div className="flex items-end justify-between">
          <div className="flex items-center gap-2 text-emerald-700 text-sm font-semibold"><Stamp className="w-5 h-5" />LUNAS / DITERIMA</div>
          <div className="text-center">
            <p className="text-xs text-[#7A7A7A] mb-8">Hormat kami,</p>
            <p className="text-sm font-semibold text-burgundy border-t border-gold/40 pt-1">Primrose Organizer</p>
          </div>
        </div>

        <div className="mt-8 pt-4 border-t border-gold/20 text-center">
          <p className="font-heading text-base text-burgundy italic">Terima kasih atas kepercayaan Anda 🌸</p>
        </div>
      </div>
      <div className="px-8 pb-6 flex justify-end gap-2 print:hidden">
        <Button variant="outline" onClick={() => downloadPdf("receipt-doc", `${rec.receipt_number}.pdf`)} data-testid="receipt-download-pdf" className="rounded-full"><Download className="w-4 h-4 mr-1" />Download PDF</Button>
        <Button onClick={() => window.print()} data-testid="receipt-print" className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full"><Printer className="w-4 h-4 mr-1" />Print</Button>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <span className="text-[#7A7A7A]">{label}</span>
    <span className="font-medium text-[#1A1A1A] text-right">{value || "-"}</span>
  </div>
);
